import { cx } from '@/utils/cx'
import { Button, DocumentCheckbox, Header, LoadingSpinner } from '@components/Generic'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import PrivacyPolicy from '@assets/privacy_policy.pdf'
import PrivacyPolicyDe from '@assets/privacy_policy_de.pdf'
import { StoreContext } from '@store/Root.store'
import { actionReport } from '@methods/actionReport'
import { axiosInstance } from '@methods/axiosConfig'
import { devLog } from '@methods/devLog'
import { AcceptanceDocument, AcceptanceDocumentRes } from '../../types/acceptanceDocument'
import { API_URL } from '@constants/apiUrl'

export const GeneralConditionsWithMicropaymentIkanoPage = observer(() => {
  const store = useContext(StoreContext)
  const { context } = store.ScenarioState

  const { language } = store.TranslationsState
  const {
    welcomePage: trans,
    klarnaPrivacyPolicyPage: policyPage,
    generalConditionsPage: generalConditionsTrans,
  } = store.TranslationsState.translationsNew

  // Checkboxes state
  const [terms, setTerms] = useState(false)
  const [privacyPolicy, setPrivacyPolicy] = useState(false)
  const [biometrics, setBiometrics] = useState(false)
  const [signed, setSigned] = useState(false)
  // const [tickAll, setTickAll] = useState(false)
  const [openBanking, setOpenBanking] = useState(false)

  // Documents fetched objects
  const [termsDoc, setTermsDoc] = useState<AcceptanceDocument>()
  const [policyDoc, setPolicyDoc] = useState<AcceptanceDocument>()
  const [biometricsDoc, setBiometricsDoc] = useState<AcceptanceDocument>()

  // Loadings state
  const [loading, setLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)

  const { isMobile } = store.AppState

  const getDocuments = async () => {
    try {
      const res = await axiosInstance.get<AcceptanceDocumentRes>(
        `${API_URL}/electronic-id/acceptance-document`,
        { withCredentials: true }
      )

      const privacyPolicy = res.data.find((item) => item.type === 'PRIVACY_POLICY')
      const termsAndConditions = res.data.find((item) => item.type === 'TERMS_AND_CONDITIONS')
      const biometrics = res.data.find((item) => item.type === 'BIOMETRIC_CONSENT')

      setTermsDoc(termsAndConditions)
      setPolicyDoc(privacyPolicy)
      setBiometricsDoc(biometrics)

      setLoading(false)
    } catch (e) {
      devLog(e)
    }
  }

  const handleTerms = ({ checked: value }: { checked: boolean }) => {
    if (value) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_CHECKED',
        payload: { documentType: 'TERMS_AND_CONDITIONS', isRequired: true },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
        payload: { documentType: 'TERMS_AND_CONDITIONS', isRequired: true },
      })
    }

    setTerms(value)
  }

  const handlePrivacyPolicy = ({ checked: value }: { checked: boolean }) => {
    if (value) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_CHECKED',
        payload: { documentType: 'PRIVACY_POLICY', isRequired: true },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
        payload: { documentType: 'PRIVACY_POLICY', isRequired: true },
      })
    }

    setPrivacyPolicy(value)
  }

  const handleOpenBanking = ({ checked: value }: { checked: boolean }) => {
    if (value) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_CHECKED',
        payload: { documentType: 'OPEN_BANKING', isRequired: true },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
        payload: { documentType: 'OPEN_BANKING', isRequired: true },
      })
    }

    setOpenBanking(value)
  }

  const handleBiometrics = ({ checked: value }: { checked: boolean }) => {
    setBiometrics(value)

    if (value) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_CHECKED',
        payload: { documentType: 'BIOMETRIC_CONSENT', isRequired: false },
      })
    } else {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_UNCHECKED',
        payload: { documentType: 'BIOMETRIC_CONSENT', isRequired: false },
      })
    }
  }

  // const handleTickAll = () => {
  //   setTickAll(!tickAll)
  //   setOpenBanking(!tickAll)
  //   setPrivacyPolicy(!tickAll)
  //   setBiometrics(!tickAll)
  //   setTerms(!tickAll)
  // }

  const handleProceed = async () => {
    try {
      setBtnLoading(true)

      await axiosInstance.post(
        `${API_URL}/electronic-id/acceptance-document`,
        {
          acceptedDocuments: biometrics
            ? [policyDoc?.id, termsDoc?.id, biometricsDoc?.id]
            : [policyDoc?.id, termsDoc?.id],
        },
        {
          withCredentials: true,
        }
      )

      setBtnLoading(false)
      store.ScenarioState.nextPage()
    } catch (e) {
      devLog(e)
    }
  }

  useEffect(() => {
    if (openBanking === true && terms === true && privacyPolicy === true) {
      setSigned(true)
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_ACCEPTED',
        payload: {},
      })
    } else {
      setSigned(false)
    }
  }, [openBanking, terms, privacyPolicy])

  useEffect(() => {
    if (context?.termsAndConditionsPresent) {
      store.ScenarioState.nextPage()
    } else if (window.location.pathname.split('/')[1] !== 'mock') {
      getDocuments()
    }
  }, [])

  return (
    <div className={cx('page-container', 'mobile-m-0', 'mobile-p-14-40')}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Header className="m-0-0-30">{trans.header}</Header>
          <p className="generic__intro-paragraph">
            {trans.descriptionPartOne}
            <a
              className="generic__link"
              href="https://eidas.ec.europa.eu/efda/tl-browser/#/screen/tl/ES/42"
              target="_blank"
              rel="noreferrer"
            >
              {trans.serviceProvider}
            </a>
          </p>

          <div className="ikano__logo-divider" />

          {policyDoc?.id && (
            <DocumentCheckbox
              inputName="privacy-policy"
              description={policyDoc?.agreement}
              externalUrl={policyDoc?.s3Url}
              handleDecision={handlePrivacyPolicy}
              value={privacyPolicy}
              goToDocumentLabel={generalConditionsTrans.goToDocument}
            />
          )}

          {termsDoc?.id && (
            <DocumentCheckbox
              inputName="terms-checkbox"
              description={termsDoc?.agreement}
              externalUrl={termsDoc?.s3Url}
              handleDecision={handleTerms}
              value={terms}
              goToDocumentLabel={generalConditionsTrans.goToDocument}
            />
          )}

          {/* Open Banking */}
          <DocumentCheckbox
            inputName="iddata-checkbox"
            description={policyPage.consent}
            externalUrl={language === 'de' ? PrivacyPolicyDe : PrivacyPolicy}
            handleDecision={handleOpenBanking}
            value={openBanking}
            goToDocumentLabel={generalConditionsTrans.goToDocument}
          />

          {biometricsDoc?.id && (
            <DocumentCheckbox
              inputName="biometrics-checkbox"
              description={biometricsDoc?.agreement}
              externalUrl={biometricsDoc?.s3Url}
              handleDecision={handleBiometrics}
              value={biometrics}
              goToDocumentLabel={generalConditionsTrans.goToDocument}
            />
          )}

          <div className="layout__content-container-bottom-fixed">
            {!isMobile ? <div className="layout__content-container-bottom-left" /> : <div />}

            <Button id="btn-proceed" onClick={handleProceed} disabled={!signed}>
              {btnLoading && <LoadingSpinner inButton />}
              <span
                className={cx(
                  'generic__button-text',
                  btnLoading && 'generic__button-text--loading'
                )}
              >
                {trans.proceed}
              </span>
            </Button>
          </div>
          <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
        </>
      )}
    </div>
  )
})
